import React from 'react';
import { IdentityValidationCanceledStatus } from 'commons/enums';
import useShowProofOfLifeErrorAlert from 'commons/hooks/useShowProofOfLifeErrorAlert';
import { TrialPeriod } from 'commons/services/configurationService/configurationServiceResponses';
import { KycDocumentErrorsType, ValidationStatusType } from 'commons/types';
import { BlockedAlert } from './BlockedAlert';
import { CanceledAlert } from './CanceledAlert';
import { DeniedAlert } from './DeniedAlert';
import { PendingAlert } from './PendingAlert';
import { PendingDocIdentityAlert } from './PendingDocIdentityAlert';
import { PendingProofOfLifeAlert } from './PendingProofOfLifeAlert';
import { PendingReviewAlert } from './PendingReviewAlert';
import { PendingTrialPeriodAlertPage } from './PendingTrialPeriodAlert';

interface InterfaceIdentityAlert {
  identityStatus: ValidationStatusType;
  statusDetail?: string;
  updatedAt: string;
  kycDocumentsErrors: KycDocumentErrorsType[];
  trialPeriod?: TrialPeriod;
  page: PendingTrialPeriodAlertPage;
  blockedTrialPeriod?: boolean;
}

const isCanceledStatus = (statusDetail?: string): boolean => {
  switch (statusDetail) {
    case IdentityValidationCanceledStatus.CANCELED:
    case IdentityValidationCanceledStatus.CANCELED_BY_MERCHANT:
    case IdentityValidationCanceledStatus.CANCELED_BY_DLOCAL:
      return true;
    default:
      return false;
  }
};

const IdentityAlert = ({
  identityStatus,
  statusDetail,
  updatedAt,
  kycDocumentsErrors,
  trialPeriod,
  page,
  blockedTrialPeriod,
}: InterfaceIdentityAlert): JSX.Element | null => {
  const proofOfLifeErrorAlert = useShowProofOfLifeErrorAlert();
  const showPendingProofOfLifeAlert = proofOfLifeErrorAlert.show;

  if (showPendingProofOfLifeAlert)
    return (
      <PendingProofOfLifeAlert
        proofOfLifeUrl={proofOfLifeErrorAlert.proofOfLifeUrl}
        isProofOfLifeUrlLoading={proofOfLifeErrorAlert.isProofOfLifeUrlLoading}
        errorCode={proofOfLifeErrorAlert.errorCode}
        page={page}
      />
    );

  switch (identityStatus) {
    case 'pending':
      return (
        <PendingAlert
          trialPeriod={trialPeriod}
          page={page}
          blockedTrialPeriod={blockedTrialPeriod}
        />
      );
    case 'pending_documentation':
      return (
        <PendingDocIdentityAlert
          kycDocumentsErrors={kycDocumentsErrors}
          blockedTrialPeriod={blockedTrialPeriod}
          page={page}
        />
      );
    case 'pending_review':
      return (
        <PendingReviewAlert
          updatedAt={updatedAt}
          blockedTrialPeriod={blockedTrialPeriod}
          page={page}
        />
      );
    case 'blocked':
      return <BlockedAlert page={page} />;
    case 'denied':
      if (isCanceledStatus(statusDetail)) {
        return <CanceledAlert page={page} />;
      }
      return <DeniedAlert page={page} />;
    case 'active':
      return null;
    default:
      return null;
  }
};

export default IdentityAlert;
